import type { ConsignmentReconciliationResponse } from '../types/venue';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SupplierStatementRow {
  displayName: string;
  openingQty: number;
  receivedQty: number;
  closingQty: number;
  consumedQty: number;
  unitCost: number | null;
  amount: number | null;
}

export interface SupplierStatementData {
  statementNumber: string;
  accountName: string;
  supplierName: string | null;
  periodFrom: string;
  periodTo: string;
  generatedAt: string;
  rows: SupplierStatementRow[];
  totalConsumed: number;
  totalAmount: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function statementNumber(accountName: string, periodTo: string): string {
  const code = accountName.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 8);
  const ym   = periodTo.slice(0, 7).replace('-', '');
  return `CONS-${code}-${ym}`;
}

// ── Transform ─────────────────────────────────────────────────────────────────
//
// Cost basis: weighted average of consumed lot quantities × their unitCostAtReceipt.
//
// Aggregation is done at lot level first ((inventoryItemId, lotId) pairs), then
// rolled up to item level. This is required because closing stock represents the
// lots that were NOT consumed — using closing lot costs to price consumed units
// produces systematically wrong billing when lots have differing receipt costs.
//
// Lot cost source priority:
//   1. unitCostAtReceipt from closing balance item (lot still on hand)
//   2. unitCostAtReceipt from opening balance item (lot fully consumed)
//   3. unitCost from a positive movement row (lot received-and-consumed in period)

export function buildSupplierStatementData(
  report: ConsignmentReconciliationResponse,
): SupplierStatementData {
  // ── Step 1: lot-level aggregation ─────────────────────────────────────────

  type LotEntry = {
    itemId:      string;
    displayName: string;
    lotId:       string | null;
    openingQty:  number;
    closingQty:  number;
    receivedQty: number;
    unitCost:    number | null;
  };

  const lotKey = (itemId: string, lotId: string | null) =>
    `${itemId}|${lotId ?? '_no_lot'}`;

  const lotMap = new Map<string, LotEntry>();

  const getLot = (itemId: string, lotId: string | null, displayName: string): LotEntry => {
    const k = lotKey(itemId, lotId);
    if (!lotMap.has(k)) {
      lotMap.set(k, { itemId, displayName, lotId, openingQty: 0, closingQty: 0, receivedQty: 0, unitCost: null });
    }
    return lotMap.get(k)!;
  };

  for (const item of report.openingStock.items) {
    const lot = getLot(item.inventoryItemId, item.lotId, item.displayName ?? item.inventoryItemId);
    lot.openingQty += item.quantity;
    if (lot.unitCost == null && item.unitCostAtReceipt != null) {
      lot.unitCost = item.unitCostAtReceipt;
    }
  }

  for (const item of report.closingStock.items) {
    const lot = getLot(item.inventoryItemId, item.lotId, item.displayName ?? item.inventoryItemId);
    lot.closingQty += item.quantity;
    // Closing balance is the most reliable source for unitCostAtReceipt; prefer it.
    if (item.unitCostAtReceipt != null) {
      lot.unitCost = item.unitCostAtReceipt;
    }
  }

  for (const m of report.movementsInPeriod.rows) {
    if (m.quantity > 0) {
      const lotId = m.lot?.lotId ?? null;
      const lot = getLot(m.inventoryItem._id, lotId, m.inventoryItem.displayName ?? m.inventoryItem._id);
      lot.receivedQty += m.quantity;
      // Use movement unitCost only as last resort (lot received-and-consumed in period
      // with no balance record to carry unitCostAtReceipt).
      if (lot.unitCost == null && m.unitCost != null) {
        lot.unitCost = m.unitCost;
      }
    }
  }

  // ── Step 2: roll up to item level ─────────────────────────────────────────

  type ItemEntry = {
    displayName:       string;
    openingQty:        number;
    receivedQty:       number;
    closingQty:        number;
    consumedCostTotal: number; // sum(consumed_lot_qty × unitCost) for lots with known cost
    consumedCostQty:   number; // sum(consumed_lot_qty) for lots with known cost
  };

  const itemMap = new Map<string, ItemEntry>();

  for (const lot of Array.from(lotMap.values())) {
    const consumedLotQty = lot.openingQty + lot.receivedQty - lot.closingQty;

    const ex = itemMap.get(lot.itemId);
    const costContrib =
      consumedLotQty > 0 && lot.unitCost != null
        ? { total: consumedLotQty * lot.unitCost, qty: consumedLotQty }
        : { total: 0, qty: 0 };

    if (ex) {
      ex.openingQty        += lot.openingQty;
      ex.receivedQty       += lot.receivedQty;
      ex.closingQty        += lot.closingQty;
      ex.consumedCostTotal += costContrib.total;
      ex.consumedCostQty   += costContrib.qty;
    } else {
      itemMap.set(lot.itemId, {
        displayName:       lot.displayName,
        openingQty:        lot.openingQty,
        receivedQty:       lot.receivedQty,
        closingQty:        lot.closingQty,
        consumedCostTotal: costContrib.total,
        consumedCostQty:   costContrib.qty,
      });
    }
  }

  // ── Step 3: build rows ────────────────────────────────────────────────────

  const rows: SupplierStatementRow[] = [];
  for (const [, item] of Array.from(itemMap.entries())) {
    const consumedQty = item.openingQty + item.receivedQty - item.closingQty;
    const unitCost =
      item.consumedCostQty > 0
        ? Math.round((item.consumedCostTotal / item.consumedCostQty) * 10000) / 10000
        : null;
    const amount =
      unitCost != null && consumedQty > 0
        ? Math.round(consumedQty * unitCost * 100) / 100
        : null;

    rows.push({
      displayName: item.displayName,
      openingQty:  item.openingQty,
      receivedQty: item.receivedQty,
      closingQty:  item.closingQty,
      consumedQty,
      unitCost,
      amount,
    });
  }

  rows.sort((a, b) => a.displayName.localeCompare(b.displayName));

  const totalConsumed = rows.reduce((s, r) => s + r.consumedQty, 0);
  const rowsWithAmount = rows.filter(r => r.amount != null);
  const totalAmount =
    rowsWithAmount.length > 0
      ? Math.round(rowsWithAmount.reduce((s, r) => s + (r.amount ?? 0), 0) * 100) / 100
      : null;

  return {
    statementNumber: statementNumber(report.header.ownershipAccount.name, report.header.periodTo),
    accountName:     report.header.ownershipAccount.name,
    supplierName:    report.header.supplier?.name ?? null,
    periodFrom:      report.header.periodFrom,
    periodTo:        report.header.periodTo,
    generatedAt:     report.header.generatedAt,
    rows,
    totalConsumed,
    totalAmount,
  };
}

export const SUPPLIER_STATEMENT_NOTE =
  'This statement summarizes consigned inventory consumed during the reporting period ' +
  'and may be used for supplier invoicing.';
